<template>
  <!-- PROJECTS -->
  <v-expansion-panel class="project-panel hoverable">
    <!-- HEADER -->
    <v-expansion-panel-header class="project-header">
      <v-hover v-slot:default="{ hover }">
        <v-row no-gutters class="project-details">
          <v-col sm="3" class="d-flex align-center">
            <template v-if="projectShareInfo">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="mr-2" :class="$vuetify.breakpoint.lgAndUp ? 'isShared' : 'isShared'" color="#818181" small v-bind="attrs" v-on.native="on">mdi-arrow-bottom-left</v-icon>
              </template>
              <span>
                {{$t('sharedBy').toString() |  capitalize  }} {{projectShareInfo && projectShareInfo.sharedBy}} {{$t('dateOn')}} {{projectShareInfo && projectShareInfo.sharedAt}}
              </span>
            </v-tooltip>
            </template>
          <!-- tooltip TODO add -->
            <div data-cy="project-name" class="d-inline project-name font-weight-bold">{{ project.name | uppercase }}</div>
          </v-col>
          <v-col v-show="$vuetify.breakpoint.smAndUp" sm="3" class="d-flex align-center">
            <div
              class="d-inline project-country font-weight-medium ml-2"
            >{{ (getCountryName(project)) | uppercase }}</div>
          </v-col>
          <v-col xs="3" class="d-flex align-center">
            <div class="align-center scenario-count font-weight-medium ml-2">{{ scenarioCount }}</div>
          </v-col>
          <v-expand-transition>
            <v-col xs="3" sm="3" class="d-sm-flex align-center justify-end">
              <v-btn icon large class="hov-btn" v-if="hover" @click.stop="newScenario" v-show="projectAuthrization">
                <FontIcon class="cmd-icon">mdi-plus</FontIcon>
              </v-btn>
              <v-btn v-if="hover" @click.stop="editProject" icon class="hov-btn" large>
                <FontIcon class="cmd-icon">mdi-pencil</FontIcon>
              </v-btn>
              <v-btn
                v-if="hover"
                @click.stop="deleteProject(project.uuid)"
                icon
                class="hov-btn"
                large
              >
                <FontIcon class="cmd-icon">mdi-delete</FontIcon>
              </v-btn>
              <v-btn
                v-if="project.hasEnergyEfficiency"
                icon
                x-large
                class="project-efficiency-mark"
              >
                <FontIcon color="forestgreen">energy_efficiency</FontIcon>
              </v-btn>
            </v-col>
          </v-expand-transition>
        </v-row>
      </v-hover>
    </v-expansion-panel-header>

    <!-- CONTENT -->
    <v-expansion-panel-content>
      <v-container fluid>
        <v-row v-if="projectAuthrization">
          <!-- EXISTING SCENARIOS -->
          <ProjectScenario
            v-for="scenario in (scenarios || [])"
            :scenario="scenario"
            :key="scenario.uuid"
            :duplicated="duplicatedScenarios.has(scenario.uuid)"
            :project="project"
          />

          <!-- NEW SCENARIO -->
          <v-col cols="12" sm="4" md="3" lg="3">
            <v-hover v-slot:default="{ hover }">
              <v-card
                data-cy="addScenarioButton"
                class="add-scenario hoverable"
                :color="buttonColor"
                outlined
                style="border-style: dashed"
                max-height="350"
                min-height="350"
                @click="newScenario"
              >
                <div class="justify-center item-emph">
                  <span>
                    <v-icon :color="buttonColor">mdi-plus</v-icon>
                  </span>
                </div>
                <v-fade-transition>
                  <v-overlay v-if="hover" color="white" absolute opacity=".4"></v-overlay>
                </v-fade-transition>
              </v-card>
            </v-hover>
          </v-col>
        </v-row>
       <v-row v-else><AlertRow :message="$t('project.unauthorization')"/></v-row>
      </v-container>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import FontIcon from '@/components/FontIcon.vue';
import ProjectScenario from '@/components/Projects/ProjectScenario.vue';
import { ScenarioMeta } from '@/classes/ScenarioMeta';
import { EventBus, EventBusEvents } from '@/helpers/event-bus';
import { ProjectMeta } from '@/classes/ProjectMeta';
import { CountryMeta } from '@/classes/CountryMeta';
import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import _color, { hexToRgba } from '@/helpers/colors';
import AlertRow from '../AlertRow.vue';
import { ProjectType, ProjectTypeRef } from '@/dto/scenario/ProjectReference';

@Component({
  components: {
    AlertRow,
    FontIcon,
    ProjectScenario
  },
  filters: {
    uppercase: (v: string): string => v.toUpperCase(),
    capitalize: function(value: any) {
      if (!value) return '';
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
  }
})
export default class ProjectPanel extends Vue {
  @Prop({ required: true, default: null })
  public project!: ProjectMeta;

  @Prop({ required: true, default: () => new Map() })
  public countries!: Map<string, CountryMeta>;

  @Prop({ required: false, default: () => new Map() })
  public duplicatedScenarios: Map<string, ScenarioExt> | undefined;

  get projectShareInfo() {
    if (this.project.sharedAt && this.project.sharedBy) {
      return {
        sharedAt: new Date(this.project.sharedAt).toLocaleString(this.$i18n.locale),
        sharedBy: this.project.sharedBy
      };
    }
    return null;
  }
  get scenarios(): ScenarioMeta[] | undefined {
    return this.project ? this.project.scenarios : undefined;
  }

  get buttonColor(): string | null {
    return hexToRgba(`${_color.pinkButton}22`);
  }

  get scenarioCount(): any {
    const count = this.project!.scenarios!.length;
    if (count > 1) {
      return `${count} ${this.$t('scenarios')}`;
    } else {
      return `${count} ${this.$t('one_scenario')}`;
    }
  }

  get projectAuthrization():boolean {
    const userGroup:Array<string> = this.$store.getters['user/getUserGroups'];
    if ([ProjectTypeRef.ren_fr_pv_project, ProjectTypeRef.ren_fr_wind_project].includes(this.project.typeRef as ProjectTypeRef)) {
      return userGroup.indexOf(ProjectType.ren_fr_project) > -1;
    }
    return userGroup.indexOf(this.project.typeRef as string) > -1;
  }

  private newScenario() {
    this.$emit('newScenario', this.project && this.project.uuid);
  }

  private editProject() {
    EventBus.$emit(EventBusEvents.editProject, this.project);
  }

  private deleteProject(projectId: string) {
    this.$emit('deleteProject', projectId);
  }

  private getCountryName(project: ProjectMeta): string {
    const country =
      (this.countries &&
        project.country &&
        this.countries.get(project.country)) ||
      null;
    const countryName =
      (country && (country as any)[this.$i18n.locale]) || project.country || '';
    return countryName;
  }
}
</script>

<style lang="stylus">
@import '../../stylus/main.styl';

.project-panel {
  &:hover {
    .project-header {
      background-color: #EEEEEE88;
    }
  }

  .project-details {
    vertical-align: middle;

    .project-name {
      color: $grey-color;
    }

    .project-country {
      color: $grey-color;
      opacity: 0.7;
    }

    .scenario-count {
      color: $grey-color;
      opacity: 0.7;
    }

    .d-flex {
      height: 55px;
    }
  }

  .add-scenario {
    position: relative;
    cursor: pointer;

    .item-emph {
      position: absolute;
      height: 100%;
      width: 100%;
      text-align: center;
      vertical-align: middle;

      span {
        position: absolute;
        text-align: center;
        display: inline-block;
        background-color: white;
        border-radius: 50%;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);

        .v-icon {
          font-size: 50px;
        }
      }
    }
  }

  .project-efficiency-mark {
    margin: 0px 7px;
  }

  .hov-btn {
    margin: 0px 7px;

    .v-btn__content .v-icon {
      color: $deep-blue;
    }

    .v-btn__content .v-icon:hover {
      color: white;
    }

    &:hover {
      background: $deep-blue;
    }
  }
}
.isShared {
  // position absolute;
  // left: -10px;
  // top: -0px;
  // transform translateY(50%);
  background-color #c2c3d1;
  // box-shadow 2px 2px 5px black
  // border-radius: 5px;
  // // animation: scalin 1s ease-in-out 4 alternate;
  // padding: 2px;
}
@keyframes scalin {
  0% {
    right: -30px;
    padding: 3px;
    background-color white
  }
  100% {
    right: -40px;
    padding: 8px;
    background-color #EC5785
  }
}
.isShared-small-screen {
  position absolute;
  right: -0px;
  top: -0px;
  background transparentify;
  padding: 3px;
  animation: pinkin 1s ease-in-out 4 alternate;
}
@keyframes pinkin {
  0% {
    background-color white
  }
  100% {
    background-color #EC5785
  }
}
</style>
